import { validDialogueSpans, type DialogueSpan } from "./dialogue.js";
/** Cosmetic presentation only. A performance cannot authorize a world/quest action. */
export interface DialoguePerformanceV1 {
  version: 1;
  sceneId: string;
  turnId: string;
  libraryVersion: string;
  seed: number;
  segments: { id: string; text: string; deliveryId: string; spans?: DialogueSpan[] }[];
  resume: "listen" | "activity";
}

const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const id = (v: unknown): v is string => typeof v === "string" && /^[a-zA-Z0-9_.:-]{1,80}$/.test(v);

/** Bound input before compiling. Unknown delivery/library IDs degrade to neutral acting. */
export function isDialoguePerformance(v: unknown): v is DialoguePerformanceV1 {
  if (!record(v) || v.version !== 1 || !id(v.sceneId) || !id(v.turnId) || !id(v.libraryVersion) ||
      !Number.isInteger(v.seed) || (v.seed as number) < 0 || (v.seed as number) > 0xffffffff ||
      (v.resume !== "listen" && v.resume !== "activity") ||
      !Array.isArray(v.segments) || v.segments.length < 1 || v.segments.length > 3) return false;
  const seen = new Set<string>();
  let length = 0;
  for (const s of v.segments) {
    if (!record(s) || !id(s.id) || seen.has(s.id) || !id(s.deliveryId) || typeof s.text !== "string" ||
        !s.text.trim() || s.text.length > 320 || /[\u0000-\u0008\u000b-\u001f\u007f]/.test(s.text)) return false;
    seen.add(s.id);
    if (s.spans !== undefined && !validDialogueSpans(s.text, s.spans)) return false;
    length += s.text.length;
  }
  return length <= 600;
}
